import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";

const getUserBalanceEndpoint =
  "https://distribution-calculator-256611876551.europe-west1.run.app/get_user_balance_solana";

const safeParse = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Format date as "Sep '24" */
function formatMonthLabel(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const year = String(date.getFullYear()).slice(-2);
  return `${month} '${year}`;
}

/**
 * Fetch get_user_balance_solana, group by month, sum interest per month,
 * then build cumulative (total interest from start to end of each month).
 * Returns { labels: ["Sep '24", "Oct '24", ...], values: [0.5, 1.2, ...] } sorted oldest first.
 */
async function fetchMonthlyCumulativeInterest(walletAddress) {
  if (!walletAddress) return { labels: [], values: [] };
  try {
    const res = await fetch(`${getUserBalanceEndpoint}?user_address=${walletAddress}`);
    const json = await res.json();
    const data = json?.data || [];
    const withHash = data
      .filter(
        (r) =>
          r.distribution_transaction_hash &&
          r.distribution_transaction_hash !== "0" &&
          String(r.distribution_transaction_hash).toLowerCase() !== "none"
      )
      .filter(
        (r) =>
          r.distribution_time &&
          r.distribution_time !== "" &&
          String(r.distribution_time).toLowerCase() !== "none"
      );

    const byMonth = {};
    withHash.forEach((r) => {
      const d = new Date(r.distribution_time);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!byMonth[key]) byMonth[key] = { date: d, interest: 0 };
      byMonth[key].interest += safeParse(r.undistributed_usdi);
    });

    const sorted = Object.entries(byMonth)
      .map(([key, v]) => ({ key, date: v.date, interest: v.interest }))
      .sort((a, b) => a.date - b.date);

    let cum = 0;
    const labels = [];
    const values = [];
    sorted.forEach(({ date, interest }) => {
      cum += interest;
      labels.push(formatMonthLabel(date));
      values.push(cum);
    });

    return { labels, values };
  } catch (e) {
    console.error(e);
    return { labels: [], values: [] };
  }
}

const CHART_COLORS = {
  line: "#5BA4E6",
  text: "#5a6577",
};

const CHART_FONT = "'Playfair Display', Georgia, serif";

const PAST_POINTS = 6;

function getBlueGradient(chart) {
  if (!chart?.chartArea) return "rgba(91, 164, 230, 0.1)";
  const ctx = chart.ctx;
  const { top, bottom } = chart.chartArea;
  const gradient = ctx.createLinearGradient(0, bottom, 0, top);
  gradient.addColorStop(0, "rgba(91, 164, 230, 0)");      // bottom: light
  gradient.addColorStop(0.5, "rgba(91, 164, 230, 0.1)");
  gradient.addColorStop(1, "rgba(91, 164, 230, 0.22)");   // top (near line): darker
  return gradient;
}

function CumulativeInterestChart({ walletAddress, isLoading }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [data, setData] = useState({ labels: [], values: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await fetchMonthlyCumulativeInterest(walletAddress);
      if (!cancelled) setData(result);
    })();
    return () => { cancelled = true; };
  }, [walletAddress]);

  useEffect(() => {
    if (typeof window === "undefined" || !canvasRef.current) return;
    if (isLoading || (data.labels.length === 0 && data.values.length === 0)) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      return;
    }

    const labels = data.labels.slice(-PAST_POINTS);
    const values = data.values.slice(-PAST_POINTS);
    if (labels.length === 0 || values.length === 0) return;

    const ctx = canvasRef.current.getContext("2d");
    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Cumulative Interest (USDi)",
            data: values,
            borderColor: CHART_COLORS.line,
            backgroundColor: (context) => getBlueGradient(context.chart),
            fill: true,
            tension: 0.3,
            pointRadius: 5,
            pointHoverRadius: 7,
            pointBackgroundColor: CHART_COLORS.line,
            pointBorderColor: "#fff",
            pointBorderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#1a2b5f",
            titleColor: "#fff",
            bodyColor: "#fff",
            padding: 10,
            titleFont: { family: CHART_FONT, size: 12 },
            bodyFont: { family: CHART_FONT, size: 11 },
            callbacks: {
              label: (ctx) => {
                const v = ctx.parsed?.y;
                if (!Number.isFinite(v)) return [];
                return [`Cumulative: ${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDi`];
              },
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: CHART_COLORS.text,
              font: { family: CHART_FONT, size: 13, weight: "bold" },
              maxRotation: 0,
            },
          },
          y: {
            grid: { display: false },
            border: { display: false },
            ticks: { display: false },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data, isLoading]);

  const wrapperStyle = { width: "100%", height: "100%", minHeight: "200px" };

  if (isLoading) {
    return (
      <div style={{ ...wrapperStyle, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}>
        Loading…
      </div>
    );
  }

  if (!walletAddress || (data.labels.length === 0 && data.values.length === 0)) {
    return (
      <div style={{ ...wrapperStyle, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}>
        No cumulative interest data yet
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
    </div>
  );
}

export default CumulativeInterestChart;
